import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { SiSap, SiSalesforce } from "react-icons/si";
import { BrandOracleMark } from "../components/site/BrandOracle";
import Reveal from "../components/site/Reveal";

const PLATFORMS = [
  {
    id: "sap",
    name: "SAP BRIM",
    Icon: SiSap,
    tagline: "Billing & Revenue Innovation Management",
    blurb:
      "The gold standard for high-volume, event-based billing across telecoms, utilities and media. We design, migrate and operate BRIM stacks at scale.",
    modules: [
      { code: "SOM", name: "Subscription Order Management", desc: "Quote, contract and subscription lifecycle." },
      { code: "CC", name: "Convergent Charging", desc: "Real-time event-based rating and discounting." },
      { code: "CI", name: "Convergent Invoicing", desc: "Consolidated invoicing across pricing models." },
      { code: "FI-CA", name: "Contract Accounts Receivable", desc: "Receivables, collections and payment clearing." },
    ],
    outcomes: [
      "Event-based charging at 30k+ TPS",
      "Convergent invoices across subscription + usage",
      "Revenue assurance at the mediation layer",
      "Cutover playbook refined over 20+ rollouts",
    ],
    image:
      "https://images.pexels.com/photos/11636366/pexels-photo-11636366.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "oracle",
    name: "Oracle",
    Icon: BrandOracleMark,
    tagline: "Fusion Cloud — Revenue Transformation Solution",
    blurb:
      "Unify CPQ, subscriptions, receivables and ASC 606 revenue recognition on Oracle Fusion Cloud. From guided selling to audited books — one stack.",
    modules: [
      { code: "CPQ", name: "Configure, Price, Quote", desc: "Guided selling, pricing, approvals and proposals." },
      { code: "OSM", name: "Subscription Management", desc: "Recurring, usage and hybrid subscription billing." },
      { code: "AR", name: "Oracle Receivables", desc: "Automated billing, collections and payment." },
      { code: "RMCS", name: "Revenue Management Cloud", desc: "Automated ASC 606 / IFRS 15 revenue recognition." },
    ],
    outcomes: [
      "Quote-to-cash in one Fusion Cloud tenant",
      "Automated ASC 606 revenue recognition",
      "OIC-driven integration to CRM & finance",
      "Subscription modifications without re-contracting",
    ],
    image:
      "https://images.unsplash.com/photo-1753715613457-63127ec40824?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBkYXJrJTIwb2ZmaWNlJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzY5NTg4ODR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "salesforce",
    name: "Salesforce",
    Icon: SiSalesforce,
    tagline: "Revenue Cloud · CPQ + Billing + RLM",
    blurb:
      "Quote-to-cash natively connected to your CRM. We stand up Revenue Cloud with CPQ, Billing and Revenue Lifecycle Management in months — not years.",
    modules: [
      { code: "CPQ", name: "Configure Price Quote", desc: "Bundles, discounts, approvals and proposal docs." },
      { code: "BILL", name: "Salesforce Billing", desc: "Billing schedules, invoicing and payments." },
      { code: "RLM", name: "Revenue Lifecycle Mgmt", desc: "Modern product-to-cash data model and APIs." },
      { code: "PAY", name: "Payment Orchestration", desc: "Rules-based allocation across payment gateways." },
    ],
    outcomes: [
      "Billing schedules auto-created from orders",
      "Hybrid pricing: fixed + usage + milestone",
      "Native CRM-to-invoice traceability",
      "Time-to-market for new offers in weeks",
    ],
    image:
      "https://images.pexels.com/photos/30766684/pexels-photo-30766684.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

const Solutions = () => {
  const [active, setActive] = useState("sap");
  const current = PLATFORMS.find((p) => p.id === active);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] overflow-hidden pt-24 md:pt-32 pb-12 md:pb-20 border-b border-white/5" data-testid="solutions-hero">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute inset-0 bg-radial-cyan opacity-80" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-xp-bg" />
        <div className="container-xp relative">
          <div className="eyebrow-lg mb-5">Solutions · Order-to-Cash</div>
          <h1 className="font-display tracking-display text-[40px] md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl" data-testid="solutions-hero-title">
            The <span className="text-xp-cyan">OTC</span> stack, <br />
            opinionated and delivered.
          </h1>
          <p className="mt-8 text-xp-muted max-w-2xl">
            Order-to-Cash is the nervous system of every modern enterprise. <span className="brand">Xpace</span> delivers it on the three ERPs that define
            the category — SAP BRIM, Oracle Fusion Cloud ERP and Salesforce Revenue Cloud — and engineers them to behave
            like one.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="section-xp" data-testid="solutions-tabs-section">
        <div className="container-xp">
          <div className="flex flex-wrap gap-2 md:gap-0 md:space-x-8 border-b border-white/10 mb-12" role="tablist" data-testid="solutions-tabs">
            {PLATFORMS.map((p) => {
              const isActive = active === p.id;
              return (
                <button
                  key={p.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(p.id)}
                  data-testid={`solutions-tab-${p.id}`}
                  className={`pb-4 text-sm font-medium transition-all flex items-center gap-3 ${
                    isActive ? "text-xp-cyan border-b-2 border-xp-cyan" : "text-xp-muted hover:text-xp-text border-b-2 border-transparent"
                  }`}
                >
                  <p.Icon className="h-4 w-4" />
                  {p.name}
                </button>
              );
            })}
          </div>

          <Reveal key={current.id} className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid={`solutions-panel-${current.id}`}>
            <div className="lg:col-span-5">
              <div className="eyebrow mb-3">{current.tagline}</div>
              <h2 className="font-display tracking-display text-[32px] md:text-5xl flex items-center gap-4">
                <current.Icon className="h-8 w-8 text-xp-text" />
                {current.name}
              </h2>
              <p className="mt-5 text-xp-muted">{current.blurb}</p>

              <div className="mt-8">
                <div className="eyebrow mb-4">Outcomes we deliver</div>
                <ul className="space-y-3">
                  {current.outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-3 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-xp-cyan mt-0.5 flex-shrink-0" />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link to="/contact" className="btn-cyan mt-10" data-testid={`solutions-cta-${current.id}`}>
                Discuss {current.name} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="relative h-64 lg:h-72 mb-6 overflow-hidden rounded-sm border border-white/10">
                <div
                  className="absolute inset-0 opacity-80"
                  style={{ backgroundImage: `url('${current.image}')`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-xp-bg via-xp-bg/60 to-transparent" />
                <div className="absolute bottom-4 left-4 eyebrow">
                  modules · {current.modules.length}
                </div>
              </div>

              <div className="border border-white/10 rounded-sm overflow-hidden" data-testid={`modules-table-${current.id}`}>
                <div className="grid grid-cols-12 text-xs font-mono uppercase tracking-widest text-xp-dim bg-xp-surface px-4 py-3 border-b border-white/10">
                  <div className="col-span-2">Code</div>
                  <div className="col-span-4">Module</div>
                  <div className="col-span-6">Scope</div>
                </div>
                {current.modules.map((m) => (
                  <div
                    key={m.code}
                    className="grid grid-cols-12 px-4 py-4 border-b border-white/5 last:border-0 text-sm hover:bg-xp-surface/60 transition-colors"
                  >
                    <div className="col-span-2 font-mono text-xp-cyan">{m.code}</div>
                    <div className="col-span-4 text-xp-text font-medium">{m.name}</div>
                    <div className="col-span-6 text-xp-muted">{m.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Integrated Stack */}
      <section className="section-xp border-t border-white/5" data-testid="solutions-integrated">
        <div className="container-xp">
          <Reveal className="max-w-2xl mb-12">
            <div className="eyebrow mb-4">Integrated OTC</div>
            <h2 className="font-display tracking-display text-[32px] md:text-5xl">
              Most enterprises don't pick one. They pick two.
            </h2>
            <p className="mt-5 text-xp-muted">
              Salesforce for the front office, SAP or Oracle for the system of record. <span className="brand">Xpace</span> specialises in the
              integration architecture that makes the combined stack behave like one.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ["Front office", "Salesforce CRM + CPQ", "Leads, quotes, customer 360"],
              ["Revenue engine", "SAP BRIM or Oracle Fusion", "Subscription, billing, revenue"],
              ["Integration fabric", "MuleSoft · OIC · SAP BTP", "API-led, event-driven, resilient"],
            ].map(([k, n, d], i) => (
              <Reveal key={n} delay={i * 0.06} className="card-xp" data-testid={`integrated-${i}`}>
                <div className="eyebrow">{k}</div>
                <div className="font-display text-2xl mt-6 tracking-display">{n}</div>
                <p className="mt-3 text-sm text-xp-muted">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
